import React, { useEffect } from "react";

import { Button, Stack, Typography } from "@mui/material";
import { SplitButton } from "components/forms";
import { MaterialTooltip } from "components/tooltips/MaterialTooltip";
import { useActionHook } from "hooks";
import { useAccountSelector, useDealActions } from "store";

import { Group, MeasureData, MeasureDataGroup, MeasureDataRound } from "../Groups-form/GroupsFormContext";
import { SummaryBatch } from "./SummaryBatch";

interface Props {
  onSave: () => Promise<void> | void;
  onSkip?: () => void | undefined;
  onSaveAsDraft: () => Promise<void> | void;
  onCancel: () => void;
  totalValue: string;
  disableSubmit: boolean;
  batchId: number | undefined;
  measureData: MeasureData[];
  groups: Group[];
  dealId: number;
  roundsByDeals: Record<number, MeasureDataRound[]> | null;
}

const totalText = "Total Value: ";

export const Footer: React.FC<Props> = ({
  onSave,
  onSkip,
  onSaveAsDraft,
  onCancel,
  totalValue = "",
  disableSubmit = false,
  batchId,
  measureData,
  groups,
  dealId,
  roundsByDeals
}) => {
  const { downloadBatchHistoryAction, getDealBatchSummaryAction } = useDealActions();
  const { batch_runtime_summary } = useAccountSelector() || {};

  const { action, data, loading, setData } = useActionHook(getDealBatchSummaryAction);

  useEffect(() => {
    if (!batch_runtime_summary) return;
    if (!roundsByDeals) return;

    const filteredMeasureData = measureData.filter((item) => {
      if (!item.value) return false;
      if (!item.key) return false;
      if (item.round_id && !item.investor_id) return false;
      return true;
    });

    if (!filteredMeasureData.length && data) {
      setData(undefined);
    }

    if (filteredMeasureData.length) {
      const measureDataGrouped: MeasureDataGroup[] = groups
        .map((group) => ({
          ...group,
          data: filteredMeasureData.filter((item) => item.group_id === group.id)
        }))
        .filter((group) => group.data.length);

      const filteredRounds: Record<number, MeasureDataRound[]> = Object.entries(roundsByDeals).reduce(
        (acc, [dealId, rounds]) => {
          return {
            ...acc,
            [dealId]: rounds.filter((item) => {
              if (typeof item.id === "string" && item.is_deleted) return false;
              return true;
            })
          };
        },
        {}
      );

      action({ groups: measureDataGrouped, dealId, roundsByDeals: filteredRounds });
    }
  }, [measureData, roundsByDeals]);

  return (
    <Stack
      sx={{
        boxShadow: "3px -2px 10px 0px rgba(10, 49, 66, 0.20)",
        position: "relative",
        bgcolor: "#fff"
      }}
    >
      {batch_runtime_summary && <SummaryBatch data={data} loading={loading} />}
      <Stack
        direction="row"
        sx={{
          height: 70,
          justifyContent: "space-between",
          alignItems: "center",
          p: "0 20px",
          gap: "20px",
          flexShrink: 0
        }}
      >
        <Stack sx={{ overflow: "hidden" }}>
          <MaterialTooltip isShow title={`${totalText} ${totalValue}`}>
            <Typography
              sx={{
                fontSize: "16px",
                fontWeight: 400,
                whiteSpace: "nowrap",
                textOverflow: "ellipsis",
                overflow: "hidden"
              }}
            >
              {totalText}
              {totalValue}
            </Typography>
          </MaterialTooltip>
          {batchId && (
            <Typography
              onClick={() => downloadBatchHistoryAction(batchId)}
              sx={{
                fontSize: "14px",
                fontWeight: 400,
                color: "#26A6AE",
                cursor: "pointer"
              }}
            >
              Download Batch History
            </Typography>
          )}
        </Stack>
        <Stack direction="row" gap="20px">
          <Button variant="outlined" onClick={onCancel}>
            Cancel
          </Button>
          {onSkip && (
            <Button variant="contained" onClick={onSkip}>
              Skip
            </Button>
          )}
          <SplitButton
            enableErrorIcon={Boolean(data?.message)}
            disabled={disableSubmit}
            options={[
              {
                title: "Save",
                handleClick: onSave
              },
              {
                title: "Save as Draft",
                handleClick: onSaveAsDraft
              }
            ]}
          />
        </Stack>
      </Stack>
    </Stack>
  );
};
